import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AvvisoAzione, AzioneAssistente, CampoCambiato, ElencoAzioni, EsitoAnnulla, SchedaLettaDto,
} from '@masamune/shared';
import type { IconName } from '../../components/ui/Icon';
import { api } from '../../lib/api';
import { toast, useToastStore } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { openModal } from '../../stores/modal.store';
import { useAuthStore } from '../../stores/auth.store';
import { annullaAzione, azioniQuery, dividi, puoRispondere, riscrivi, statoAssistenteQuery } from './assistenteApi';

/**
 * Il registro «Cosa ha fatto MUNE», la spesa del mese e i tasti dell'Assistente nel
 * dettaglio di una task: Riscrivi titolo e descrizione, Dividi in più task (28/10/2026,
 * os-planning/ai/fase-1-progetto.md, decisioni 10, 12 e 15). Le chiamate all'API stanno in
 * assistenteApi.ts; qui gli hook e le parole a schermo, in un .ts e non nei componenti:
 * un .tsx che esporta anche costanti o funzioni rompe il Fast Refresh (schermo bianco).
 */

// ── Chi vede i tasti dell'Assistente ─────────────────────────────────────────

/**
 * Vero solo per gli Amministratori e solo quando l'Assistente può rispondere (attivo o, in
 * locale, finto). La query dello stato si accende solo per loro: agli altri /ai/stato
 * risponde 403, e la Squadra farebbe una chiamata in errore a ogni task aperta.
 * Ad Assistente spento il gestionale resta identico a prima.
 */
export function useAssistenteAttivo(): boolean {
  const amministratore = useAuthStore((s) => s.user?.role.key === 'SUPER_ADMIN');
  const { data: stato } = useQuery({ ...statoAssistenteQuery, enabled: amministratore });
  return amministratore && puoRispondere(stato);
}

/** Dopo una chiamata a pagamento: la spesa di oggi e del mese in Impostazioni si rifà. */
export function rinfrescaSpesa(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['ai', 'stato'] });
  qc.invalidateQueries({ queryKey: ['ai', 'consumi'] });
}

// ── Riscrivi titolo e descrizione (decisione 12) ─────────────────────────────

/** La proposta di Riscrivi, coi due campi che la persona può correggere prima di usarla. */
export interface PropostaRiscrittura {
  taskId: string;
  aiActionId: string;
  titolo: string;
  descrizione: string;
  /** Cosa ha tolto il server, già in parole: «Ho tolto una password dal testo: va nel Vault». */
  tolto: string[];
  lette: SchedaLettaDto[];
  finta: boolean;
}

/**
 * Lo stato di Riscrivi per la task aperta, condiviso da menu ⋯ e blocco Descrizione. La
 * chiamata parte solo dal clic (useMutation): mai da un effetto, che in locale con
 * React.StrictMode partirebbe due volte. La proposta non salva niente da sola: «Usa questo
 * testo» manda il testo dei campi (anche corretto) con l'aiActionId; «Lascia com'è» non
 * chiama niente. Il dettaglio resta montato passando da una task all'altra, quindi la
 * proposta vale solo per la task da cui è partita.
 */
export function useRiscriviTask(taskId: string | undefined) {
  const qc = useQueryClient();
  const [proposta, setProposta] = useState<PropostaRiscrittura | null>(null);

  const chiamata = useMutation({
    mutationFn: (id: string) => riscrivi(id),
    onSuccess: (r, id) => {
      setProposta({
        taskId: id,
        aiActionId: r.aiActionId,
        titolo: r.titolo ?? '',
        descrizione: r.descrizione ?? '',
        tolto: r.tolto ?? [],
        lette: r.lette ?? [],
        finta: !!r.finta,
      });
      rinfrescaSpesa(qc);
    },
    // 402 col budget finito, 503 ad Assistente spento, 403 su una task privata: la frase la scrive l'API.
    onError: (e: any) => toast.alert('Testo non riscritto', e?.message ?? 'Riprova'),
  });

  const salvataggio = useMutation({
    mutationFn: (v: { proposta: PropostaRiscrittura; titolo: string; descrizione: string | null; frase: string }) =>
      api.patch(`/tasks/${encodeURIComponent(v.proposta.taskId)}`, { title: v.titolo, description: v.descrizione, aiActionId: v.proposta.aiActionId }),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['project'] });
      qc.invalidateQueries({ queryKey: ['ai', 'azioni'] });
      setProposta((p) => (p?.aiActionId === v.proposta.aiActionId ? null : p));
      // 6 secondi di Annulla nel toast; poi resta in «Cosa ha fatto MUNE».
      toast.undo(v.frase, { onUndo: () => annullaSubito(qc, v.proposta.aiActionId) });
    },
    // 409 «già usata» o «scaduta: riscrivi di nuovo»: la proposta resta a schermo, si può lasciare.
    onError: (e: any, v) => {
      // Il testo nuovo è salvato, poi qualcosa non è andato (28/10/2026): la proposta è usata,
      // e la frase del server dice dove si annulla.
      if (e?.code === 'AI_A_META') {
        qc.invalidateQueries({ queryKey: ['tasks'] });
        qc.invalidateQueries({ queryKey: ['project'] });
        qc.invalidateQueries({ queryKey: ['ai', 'azioni'] });
        setProposta((p) => (p?.aiActionId === v.proposta.aiActionId ? null : p));
        toast.warn('Testo salvato, poi un errore', {
          description: e.message,
          duration: 15_000,
          action: { label: 'Annulla', onClick: () => annullaSubito(qc, v.proposta.aiActionId) },
        });
        return;
      }
      // Scaduta o già usata (28/10/2026): la proposta esce, se no ogni clic darebbe lo stesso
      // errore e «Riscrivi titolo e descrizione» resterebbe nascosto nel menu.
      if (e?.code === 'AI_PROPOSTA') {
        qc.invalidateQueries({ queryKey: ['tasks'] });
        setProposta((p) => (p?.aiActionId === v.proposta.aiActionId ? null : p));
        toast.alert('Proposta di MUNE non più valida', e.message);
        return;
      }
      toast.alert('Testo non salvato', e?.message ?? 'Riprova');
    },
  });

  const qui = proposta && proposta.taskId === taskId ? proposta : null;

  return {
    proposta: qui,
    /** La chiamata per questa task sta lavorando (la mascotte che pensa, «Riscrivo…»). */
    inCorso: chiamata.isPending && chiamata.variables === taskId,
    salvo: salvataggio.isPending,
    chiedi: (opzioni?: { onError?: () => void }) => {
      if (!taskId) return;
      chiamata.mutate(taskId, { onError: () => opzioni?.onError?.() });
    },
    correggi: (campi: Partial<Pick<PropostaRiscrittura, 'titolo' | 'descrizione'>>) =>
      setProposta((p) => (p ? { ...p, ...campi } : p)),
    lascia: () => setProposta(null),
    /** «Usa questo testo»: `prima` è il testo di adesso, per dire nel toast cosa è cambiato. */
    usa: (prima: { titolo: string; descrizione: string | null }, opzioni?: { onFatto?: () => void }) => {
      if (!qui) return;
      const titolo = qui.titolo.trim();
      const descrizione = qui.descrizione.trim() || null;
      if (!titolo) return;
      const cambiaTitolo = titolo !== prima.titolo;
      const cambiaDescrizione = (descrizione ?? '') !== (prima.descrizione ?? '').trim();
      if (!cambiaTitolo && !cambiaDescrizione) {
        // Uguale a com'è adesso: niente da salvare (la proposta scade da sola, come con «Lascia com'è»).
        setProposta(null);
        toast.info('Niente da cambiare', 'Il testo proposto è uguale a quello di adesso.');
        opzioni?.onFatto?.();
        return;
      }
      const frase = cambiaTitolo && cambiaDescrizione ? 'Titolo e descrizione riscritti' : cambiaTitolo ? 'Titolo riscritto' : 'Descrizione riscritta';
      salvataggio.mutate({ proposta: qui, titolo, descrizione, frase }, {
        onSuccess: () => opzioni?.onFatto?.(),
        // Salvato e poi fermato a metà: la proposta non c'è più, il pannello si chiude lo stesso.
        onError: (e: any) => { if (e?.code === 'AI_A_META') opzioni?.onFatto?.(); },
      });
    },
  };
}

// ── Dividi in più task (decisione 10) ────────────────────────────────────────

export interface TaskDaDividere {
  id: string;
  title: string;
  description: string | null;
  projectId: string | null;
  clientId: string | null;
  /** Un lavoro previsto: la finestra parte con «Completa la task di partenza» tolta (28/10/2026). */
  inScope?: boolean;
  /** Si ripete: la finestra dice che completarla fa nascere subito la prossima. */
  recurrence?: string | null;
}

/** Quello che si manda da dividere: titolo, a capo, descrizione. */
export function testoDaDividere(t: Pick<TaskDaDividere, 'title' | 'description'>): string {
  const d = t.description?.trim();
  return d ? `${t.title}\n${d}` : t.title;
}

/**
 * «Dividi in più task» dal menu ⋯: la chiamata parte dal clic, con un toast «Divido…» finché
 * lavora (la mascotte non entra in una voce del menu: ActionItem vuole un'icona); poi si apre
 * Nuovo task con la proposta, nel prefill concordato con la finestra { clientId, testo,
 * partenzaId, proposta }. «Assegnato a» resta quello di sempre (chi crea). La spunta
 * «Completa la task di partenza» la mette la finestra.
 */
export function useDividiInPiuTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (t: TaskDaDividere) =>
      dividi({ testo: testoDaDividere(t), partenzaId: t.id, projectId: t.projectId, clientId: t.clientId }),
    // Due minuti: la chiamata ha un tempo massimo di 30 secondi con un solo nuovo tentativo.
    onMutate: (t) => toast.info('Divido…', { description: `«${t.title}» in più task`, duration: 120_000 }),
    onSettled: (_r, _e, _t, idToast) => { if (idToast) useToastStore.getState().dismiss(idToast); },
    onSuccess: (proposta, t) => {
      rinfrescaSpesa(qc);
      openModal('newTask', {
        projectId: t.projectId ?? undefined,
        prefill: {
          clientId: t.clientId ?? undefined,
          testo: testoDaDividere(t),
          partenzaId: t.id,
          proposta,
          partenzaPrevista: !!t.inScope,
          partenzaRicorrente: !!t.recurrence && t.recurrence !== 'NONE',
        },
      });
    },
    onError: (e: any) => toast.alert('Task non diviso', e?.message ?? 'Riprova'),
  });
}

// ── L'Annulla ────────────────────────────────────────────────────────────────

/**
 * Dopo un Annulla tornano com'erano task, progetti e schede: si rileggono, col registro.
 * Dalla 2D (29/10/2026) MUNE scrive anche note del cliente, Diario e trattative, quindi
 * si rileggono anche la scheda del cliente e le sue note, la scheda contatto del telefono,
 * i numeri del progetto (le comunicazioni contano) e le trattative, come fa useDealActions.
 * Serve uguale dopo un'azione della chat o una carta confermata. Le query che nessuna
 * pagina sta mostrando si segnano soltanto: si rileggono quando si riaprono.
 */
export function rinfrescaDopoAnnulla(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['tasks'] });
  qc.invalidateQueries({ queryKey: ['project'] });
  qc.invalidateQueries({ queryKey: ['ai', 'azioni'] });
  qc.invalidateQueries({ queryKey: ['ai', 'memoria'] });
  qc.invalidateQueries({ queryKey: ['project-stats'] });
  qc.invalidateQueries({ queryKey: ['client'] });
  qc.invalidateQueries({ queryKey: ['client-notes'] });
  qc.invalidateQueries({ queryKey: ['contact'] });
  qc.invalidateQueries({ queryKey: ['deals'] });
}

/** L'Annulla del toast (6 secondi): niente conferma, è il gesto appena fatto. */
export function annullaSubito(qc: QueryClient, id: string) {
  annullaAzione(id)
    .then((esito) => { rinfrescaDopoAnnulla(qc); toast.ok(esito.frase); })
    .catch((e: any) => { rinfrescaDopoAnnulla(qc); toast.alert('Annullamento non riuscito', e?.message ?? 'Riprova'); });
}

/** Le mail e i Discord già partiti non tornano indietro: la conferma lo dice prima. */
function avvisiCheRestano(a: AzioneAssistente): string {
  const fuori = a.avvisi.filter((v) => v.canale !== 'campanella');
  if (!fuori.length) return '';
  return fuori.length === 1
    ? ' Un avviso è già partito e resta: ' + fuori[0]!.cosa + '.'
    : ` ${fuori.length} avvisi sono già partiti e restano.`;
}

/**
 * L'Annulla del registro: chiede conferma, poi la frase dell'esito nel toast. Il server
 * rimette solo le righe ancora come le ha lasciate l'Assistente; se non c'è più niente da
 * rimettere risponde 409 con la frase, che si mostra così com'è. Si può anche ad
 * Assistente spento: l'Annulla non chiama il modello.
 */
export function useAnnullaAzione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: AzioneAssistente): Promise<EsitoAnnulla | null> => {
      const ok = await confirmDialog({
        title: 'Annullare questa azione di MUNE?',
        body: `${a.frase}. Torna com'era solo quello che nessuno ha cambiato dopo.${avvisiCheRestano(a)}`,
        confirmLabel: "Annulla l'azione",
        cancelLabel: "Lascia com'è",
        icon: 'undo',
        danger: true,
      });
      if (!ok) return null;
      return annullaAzione(a.id);
    },
    onSuccess: (esito) => {
      if (!esito) return;
      rinfrescaDopoAnnulla(qc);
      toast.ok(esito.frase);
    },
    onError: (e: any) => {
      rinfrescaDopoAnnulla(qc);
      toast.alert('Annullamento non riuscito', e?.message ?? 'Riprova');
    },
  });
}

// ── Il registro: le pagine ───────────────────────────────────────────────────

/**
 * Un'azione del registro. Dal 28/10/2026 AzioneAssistente ha anche `annullataInParte`
 * («Annullata in parte») e `perche` (perché un'azione eseguita non si annulla più): il
 * nome resta per i componenti che lo usano.
 */
export type AzioneNelRegistro = AzioneAssistente;

/**
 * Le azioni a pagine di 20 (GET /ai/azioni, il cursore è l'id dell'ultima): lo schermo ne
 * mostra 10 e poi «Mostra altre». La chiave sta sotto ['ai','azioni'], così un Annulla o un
 * salvataggio la rifanno insieme alle altre.
 */
export function useRegistroAzioni() {
  return useInfiniteQuery({
    queryKey: ['ai', 'azioni', 'pagine'] as const,
    queryFn: ({ pageParam }) => azioniQuery(pageParam).queryFn(),
    initialPageParam: null as string | null,
    getNextPageParam: (ultima: ElencoAzioni) => ultima.prossima ?? undefined,
    staleTime: 15_000,
  });
}

// ── Date in ora di Roma ──────────────────────────────────────────────────────
// Il server salva in UTC: un'azione tra mezzanotte e le 2 ora italiana, tagliata con
// slice(0, 10), finirebbe nel giorno prima. Qui si passa sempre dall'ora di Roma.

const ROMA = 'Europe/Rome';
const FMT_YMD = new Intl.DateTimeFormat('it-IT', { timeZone: ROMA, year: 'numeric', month: '2-digit', day: '2-digit' });
const FMT_ORA = new Intl.DateTimeFormat('it-IT', { timeZone: ROMA, hour: '2-digit', minute: '2-digit' });
// Il giorno già calcolato a Roma si scrive a mezzogiorno UTC: lo stesso giorno in ogni fuso.
const FMT_GIORNO_LUNGO = new Intl.DateTimeFormat('it-IT', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' });
const FMT_MESE = new Intl.DateTimeFormat('it-IT', { timeZone: 'UTC', month: 'long' });

/** 'YYYY-MM-DD' del giorno a Roma. */
export function giornoDiRoma(quando: string | Date): string {
  const parti = FMT_YMD.formatToParts(typeof quando === 'string' ? new Date(quando) : quando);
  const v = (t: Intl.DateTimeFormatPartTypes) => parti.find((p) => p.type === t)?.value ?? '';
  return `${v('year')}-${v('month')}-${v('day')}`;
}

function giornoPrima(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! - 1)).toISOString().slice(0, 10);
}

const maiuscola = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** «Oggi», «Ieri», «Martedì 27 ottobre» (con l'anno se non è quello di oggi). */
export function etichettaGiorno(ymd: string, oggi = giornoDiRoma(new Date())): string {
  if (ymd === oggi) return 'Oggi';
  if (ymd === giornoPrima(oggi)) return 'Ieri';
  const [y, m, d] = ymd.split('-').map(Number);
  const testo = FMT_GIORNO_LUNGO.format(new Date(Date.UTC(y!, m! - 1, d!, 12)));
  return maiuscola(ymd.slice(0, 4) !== oggi.slice(0, 4) ? `${testo} ${y}` : testo);
}

/** «oggi», «ieri», «27/10»: il giorno corto, nelle righe del telefono. */
export function giornoCorto(ymd: string, oggi = giornoDiRoma(new Date())): string {
  if (ymd === oggi) return 'oggi';
  if (ymd === giornoPrima(oggi)) return 'ieri';
  const [y, m, d] = ymd.split('-');
  return y !== oggi.slice(0, 4) ? `${d}/${m}/${y}` : `${d}/${m}`;
}

/** «10:42», in ora di Roma. */
export const oraDiRoma = (iso: string) => FMT_ORA.format(new Date(iso));

/** «oggi alle 10:42», «ieri alle 10:42», «il 27/10 alle 10:42». */
export function quandoDiRoma(iso: string): string {
  const g = giornoCorto(giornoDiRoma(iso));
  return `${g === 'oggi' || g === 'ieri' ? g : `il ${g}`} alle ${oraDiRoma(iso)}`;
}

/** Quando conta per il registro: quando è stata eseguita, se no quando è stata chiesta. */
export const quandoAzione = (a: AzioneAssistente) => a.eseguitaIl ?? a.il;

/** Le azioni (già dalla più nuova) per giorno di Roma, nell'ordine in cui arrivano. */
export function perGiorno<T extends AzioneAssistente>(azioni: T[]): Array<{ giorno: string; etichetta: string; azioni: T[] }> {
  const oggi = giornoDiRoma(new Date());
  const gruppi: Array<{ giorno: string; etichetta: string; azioni: T[] }> = [];
  for (const a of azioni) {
    const giorno = giornoDiRoma(quandoAzione(a));
    const ultimo = gruppi[gruppi.length - 1];
    if (ultimo && ultimo.giorno === giorno) ultimo.azioni.push(a);
    else gruppi.push({ giorno, etichetta: etichettaGiorno(giorno, oggi), azioni: [a] });
  }
  return gruppi;
}

// ── Le parole del registro ───────────────────────────────────────────────────

/** «Chiesta da Lorenzo Masamune»; quelle della notte non le ha chieste nessuno. */
export const chiLHaChiesta = (a: AzioneAssistente) => (a.chi ? `Chiesta da ${a.chi.nome}` : 'Fatta da MUNE di notte');

/**
 * Lo stato, solo quando non è semplicemente fatta: «Annullata oggi alle 11:02 da Andrea
 * Anfuso». Un'azione eseguita che non si annulla più dice perché, con le parole dell'API:
 * «Non si annulla più: Andrea l'ha cambiata il 29/10».
 */
export function testoStato(a: AzioneNelRegistro): string | null {
  switch (a.stato) {
    case 'ESEGUITA': return a.annullabile ? null : (a.perche ?? null);
    case 'ANNULLATA': {
      const quando = a.annullataIl ? ` ${quandoDiRoma(a.annullataIl)}` : '';
      const da = a.annullataDa ? ` da ${a.annullataDa}` : '';
      return `${a.annullataInParte ? 'Annullata in parte' : 'Annullata'}${quando}${da}`;
    }
    // Con delle righe: si è fermata dopo aver già fatto qualcosa, che si vede qui e si annulla.
    case 'FALLITA': return a.righe.length ? 'Non riuscita, fermata a metà' : 'Non riuscita';
    case 'PROPOSTA': return 'Proposta non ancora usata';
    case 'SCADUTA': return 'Proposta scaduta senza essere usata';
    case 'RIFIUTATA': return "Proposta lasciata com'era";
    default: return null;
  }
}

/** Lo stato in una parola, per le righe del telefono. */
export function statoBreve(a: AzioneNelRegistro): string | null {
  if (a.stato === 'ANNULLATA') return a.annullataInParte ? 'annullata in parte' : 'annullata';
  if (a.stato === 'FALLITA') return a.righe.length ? 'fermata a metà' : 'non riuscita';
  if (a.stato === 'ESEGUITA' && !a.annullabile && a.perche) return 'non si annulla più';
  return null;
}

/**
 * I tipi di riga e le cose fatte come li allarga la 2D (29/10/2026, fase-2-progetto.md,
 * «Tipi condivisi»): note del cliente, Diario, trattative ed eventi dello storico, più
 * «eliminata», «spostata» e «tolta». Scritti qui per intero, così le etichette valgono
 * prima e dopo che RigaAzione si allarga in @masamune/shared; se un giorno l'API manda un
 * tipo che qui manca, il typecheck lo dice dove si chiamano etichettaRiga e iconaRiga.
 */
type TipoRiga = 'task' | 'memoria' | 'nota' | 'diario' | 'trattativa' | 'evento';
type CosaRiga = 'creata' | 'cambiata' | 'completata' | 'aggiunta' | 'eliminata' | 'spostata' | 'tolta';

const TASK_FATTO: Record<CosaRiga, string> = {
  creata: 'Task creato', cambiata: 'Task cambiato', completata: 'Task completato', aggiunta: 'Task aggiunto',
  eliminata: 'Task eliminato', spostata: 'Task spostato', tolta: 'Task tolto',
};

const TRATTATIVA_FATTO: Record<CosaRiga, string> = {
  creata: 'Trattativa creata', aggiunta: 'Trattativa creata', cambiata: 'Trattativa cambiata', completata: 'Trattativa chiusa',
  spostata: 'Trattativa spostata', eliminata: 'Trattativa eliminata', tolta: 'Trattativa eliminata',
};

/**
 * Cosa ha fatto su una riga, detto con la cosa: «Task creato», «Aggiunto alla scheda»,
 * «Tolto dalla scheda», «Nota del cliente aggiunta», «Scritto nel Diario», «Scritto nello
 * storico della trattativa», «Trattativa spostata». Il titolo e il link della riga dicono
 * quale.
 */
export function etichettaRiga(r: { tipo: TipoRiga; cosa: CosaRiga }): string {
  const via = r.cosa === 'eliminata' || r.cosa === 'tolta';
  switch (r.tipo) {
    case 'task': return TASK_FATTO[r.cosa] ?? 'Task cambiato';
    case 'memoria': return via ? 'Tolto dalla scheda' : r.cosa === 'cambiata' ? 'Corretto nella scheda' : 'Aggiunto alla scheda';
    case 'nota': return via ? 'Nota del cliente eliminata' : r.cosa === 'cambiata' ? 'Nota del cliente corretta' : 'Nota del cliente aggiunta';
    case 'diario': return via ? 'Tolto dal Diario' : 'Scritto nel Diario';
    case 'evento': return via ? 'Tolto dallo storico della trattativa' : 'Scritto nello storico della trattativa';
    case 'trattativa': return TRATTATIVA_FATTO[r.cosa] ?? 'Trattativa cambiata';
    // Un tipo arrivato dall'API prima che il web lo conosca: meglio una parola vaga che niente.
    default: return 'Cambiato da MUNE';
  }
}

/** L'icona di una riga al telefono, la stessa delle pagine: note a matita, Diario a busta. */
const ICONA_RIGA: Record<TipoRiga, IconName> = {
  task: 'tasks', memoria: 'message', nota: 'pencil', diario: 'mail', trattativa: 'handshake', evento: 'handshake',
};
export const iconaRiga = (r: { tipo: TipoRiga }): IconName => ICONA_RIGA[r.tipo] ?? 'tasks';

// ── «tramite MUNE» nel Diario, nelle note e nello storico (2D) ──────────────

/**
 * Chi ha scritto una riga del Diario, una nota del cliente o un evento dello storico della
 * trattativa (29/10/2026, fase 2D). Quelle scritte da MUNE portano `aiActionId` (lo mette
 * l'API dal contesto dell'azione, mai dal corpo) e restano firmate da chi ha chiesto:
 * «Andrea Anfuso, tramite MUNE», come nel Log attività del progetto. `nome` è l'autore come
 * lo scrive quel posto («Andrea Anfuso», «Andrea»); senza persona, «MUNE» se l'ha scritta
 * lui, se no `senzaNome` («Sistema» nelle note).
 */
export function chiHaScritto(nome: string | null | undefined, aiActionId: string | null | undefined, senzaNome = ''): string {
  if (aiActionId) return nome ? `${nome}, tramite MUNE` : 'MUNE';
  return nome || senzaNome;
}

/** Un valore lungo (una descrizione) si mostra su più righe, accorciato, e non in fila. */
export const campoLungo = (c: CampoCambiato) =>
  [c.prima, c.dopo].some((v) => !!v && (v.length > 60 || v.includes('\n')));

/** «Scadenza: gio 29/10, poi lun 2/11»; «Scadenza: gio 29/10»; «Scadenza: gio 29/10, poi niente». */
export function testoCampo(c: CampoCambiato): string {
  if (c.prima != null && c.dopo != null) return `${c.campo}: ${c.prima}, poi ${c.dopo}`;
  if (c.dopo != null) return `${c.campo}: ${c.dopo}`;
  if (c.prima != null) return `${c.campo}: ${c.prima}, poi niente`;
  return c.campo;
}

const accorcia = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

/** In una riga sola (telefono): di un valore lungo si dice solo com'è adesso, accorciato. */
export function testoCampoCorto(c: CampoCambiato): string {
  if (!campoLungo(c)) return testoCampo(c);
  return `${c.campo}: ${accorcia((c.dopo ?? 'niente').replace(/\s+/g, ' '), 70)}`;
}

/** «Mail di assegnazione di "…" per Andrea Anfuso». */
export function testoAvviso(v: AvvisoAzione): string {
  const cosa = maiuscola(v.cosa);
  return v.a ? `${cosa} per ${v.a}` : cosa;
}

export const ICONA_AVVISO: Record<AvvisoAzione['canale'], 'mail' | 'message' | 'bell'> = {
  mail: 'mail',
  discord: 'message',
  campanella: 'bell',
};

/** «Letto prima di riscrivere: schede di Xenia ed Elios». Le schede vuote non si nominano. */
export function fraseLette(lette: SchedaLettaDto[], prima: string): string | null {
  const nomi = lette.filter((s) => s.righe > 0).map((s) => s.nome);
  if (!nomi.length) return null;
  const ultimo = nomi[nomi.length - 1]!;
  const elenco = nomi.length === 1 ? ultimo : `${nomi.slice(0, -1).join(', ')} ${/^[eE]/.test(ultimo) ? 'ed' : 'e'} ${ultimo}`;
  return `Letto prima di ${prima}: ${nomi.length === 1 ? 'scheda' : 'schede'} di ${elenco}`;
}

// ── La spesa del mese ────────────────────────────────────────────────────────
// Il nome della funzione arriva già in parole da GET /ai/consumi («Dividi le task»), il
// codice (chat, dividi, …) serve solo per l'icona al telefono (28/10/2026).

/** '2026-10' → 'ottobre'. */
export function meseInParole(mese: string): string {
  const [y, m] = mese.split('-').map(Number);
  if (!y || !m) return mese;
  return FMT_MESE.format(new Date(Date.UTC(y, m - 1, 15)));
}

/** Il mese di adesso in UTC, come i tetti del server, se la spesa non è ancora arrivata. */
export const meseDiAdesso = () => new Date().toISOString().slice(0, 7);

/** «8 $» per un budget tondo, «0,42 $» per il resto. */
export function dollariCorti(n: number): string {
  return Number.isInteger(n)
    ? `${n.toLocaleString('it-IT')} $`
    : `${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
}

export const chiamate = (n: number) => `${n} ${n === 1 ? 'chiamata' : 'chiamate'}`;

/** Quanto della barra è speso, da 0 a 100. */
export const percentoSpeso = (spesi: number, budget: number) => (budget > 0 ? Math.min(100, Math.max(0, (spesi / budget) * 100)) : 0);
